import { useMemo, useCallback, useEffect, useRef } from 'react';
import { IWorkerData } from '@th-types/worker.type';
import { BASE_PATH } from '@utils/Environment';
import { useChatContext } from '@company/state/chatContext';
import { useJobContext } from '@company/state/jobContext';
import { shareJobToWorker } from '@company/services/jobAssignment/api';
import {
  fetchMessages,
  sendMessage as sendJobMessage,
} from '@company/services/chat/api';
import { useQuery, useQueryClient, InfiniteData } from '@tanstack/react-query';
import Messages from '@th-types/messages.type';
import { AxiosError } from 'axios';
import QueryKeys from '@constants/queryKeys';
import { useUserContext } from '@state/userContext';
import { CombinedMessage } from '@th-types/message.type';
import { useMessageMutation } from '@hooks/useMessageMutation';
import { invalidateWorkerQueries } from '@company/utils/worker-modal-helper';
import useUpdateWorker from '@company/state/useUpdateWorker/useUpdateWorker';
import ITalentsData from '@company/types/talents.type';
import GenericChat from './GenericChat';

interface JobMessageChatProps {
  showWorkerInfo: boolean;
  worker: IWorkerData;
  onClose: () => void;
  onViewProfile?: () => void;
}

function JobMessageChat({
  showWorkerInfo,
  worker,
  onClose,
  onViewProfile,
}: JobMessageChatProps) {
  const {
    chatAssignmentId,
    setChatAssignmentId,
    jobChatId,
    setJobChatId,
    setChatWasCreated,
    showPrivateInfo,
    originalJobMessage,
  } = useChatContext();
  const { job } = useJobContext();
  const { id: userId } = useUserContext();
  const queryClient = useQueryClient();
  const { updateWorker, fetchUpdatedWorker } = useUpdateWorker();
  const previousChatIdRef = useRef<number | null>(null);

  // Prioritize chatAssignmentId for stable query key throughout the session
  const activeChatId = chatAssignmentId || jobChatId;

  useEffect(() => {
    if (activeChatId) {
      if (
        previousChatIdRef.current !== null &&
        previousChatIdRef.current !== activeChatId
      ) {
        queryClient.removeQueries({
          queryKey: [
            `${QueryKeys.JOB_MESSAGE_CONVERSATIONS}_${previousChatIdRef.current}`,
          ],
        });
      }
      previousChatIdRef.current = activeChatId;
    } else {
      // Chat is closing, reset the ref
      previousChatIdRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChatId]);

  const viewProfile = useCallback(() => {
    if (onViewProfile) {
      onViewProfile();
      return;
    }
    window.open(
      `${BASE_PATH}workers/${worker.customUrl ?? worker.id}`,
      '_blank'
    );
  }, [onViewProfile, worker]);

  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    error: messagesError,
  } = useQuery<Messages, AxiosError>({
    queryKey: [`${QueryKeys.JOB_MESSAGE_CONVERSATIONS}_${chatAssignmentId}`],
    // TODO: use jobChatId
    queryFn: () => fetchMessages(chatAssignmentId),
    enabled: chatAssignmentId !== null,
  });

  const combinedMessages: CombinedMessage[] = useMemo(() => {
    return messagesData
      ? [
          ...(messagesData.messages?.map((msg) => ({
            ...msg,
            type: 'message' as const,
          })) || []),
          ...(messagesData.systemMessages?.map(
            ({ type: systemType, ...msg }) => ({
              ...msg,
              systemType,
              type: 'system' as const,
            })
          ) || []),
        ].sort(
          (a, b) =>
            new Date(a.created).getTime() - new Date(b.created).getTime()
        )
      : [];
  }, [messagesData]);

  const chatMembers = useMemo(() => {
    if (!messagesData?.chatMembers) return [];
    return messagesData.chatMembers;
  }, [messagesData]);

  const { mutateAsync: sendMessage, isPending: isSendingMessage } =
    useMessageMutation({
      queryKey: `${QueryKeys.JOB_MESSAGE_CONVERSATIONS}_${activeChatId}`,
      mutationFn: async ({
        chatId,
        message,
      }: {
        chatId: number;
        message: string;
      }) => {
        return await sendJobMessage(chatId, { message });
      },
      getOptimisticMessage: ({ message }) => ({
        id: Date.now(),
        authorId: userId,
        created: new Date(),
        jobId: job?.id || 0,
        text: message,
        readBy: [],
        sending: true,
        sent: undefined,
      }),
    });

  const resendMessageMutation = useMessageMutation({
    queryKey: `${QueryKeys.JOB_MESSAGE_CONVERSATIONS}_${activeChatId}`,
    mutationFn: async ({ message }: { message: string; messageId: number }) => {
      return await sendJobMessage(activeChatId, { message });
    },
    getOptimisticMessage: ({ message }) => ({
      id: Date.now(),
      authorId: userId,
      created: new Date(),
      jobId: job?.id || 0,
      text: message,
      readBy: [],
      sending: true,
      sent: undefined,
    }),
  });

  const updateWorkerInTalentsList = useCallback(async () => {
    if (!job?.id) return;

    // Fetch fresh data for this specific worker
    await updateWorker({ workerId: worker.id, jobId: job.id });

    // Update only this worker in the cached talents list
    const talentsData = queryClient.getQueryData<
      InfiniteData<ITalentsData, unknown>
    >([QueryKeys.COMPANY_TALENTS, job.id]);

    if (talentsData) {
      await fetchUpdatedWorker({
        data: talentsData,
        queryClient,
        queryKey: [QueryKeys.COMPANY_TALENTS, job.id],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, worker.id]);

  const handleFirstMessage = useCallback(
    async (message: string) => {
      // Share job and create chat assignment
      const result = await shareJobToWorker(job?.id, worker.id);
      if (!result?.success) {
        throw new Error('Failed to create chat assignment');
      }

      const chatId = result.data.jobAssignmentId;

      // Update worker modal buttons
      invalidateWorkerQueries({
        queryClient,
        workerId: worker.id,
        jobId: job?.id,
        jobAssignmentId: chatId,
        skipJobCounters: true,
      });

      const response = await sendJobMessage(chatId, { message });
      if (!response?.success) return;

      if ('jobChatId' in response && typeof response.jobChatId === 'number') {
        setJobChatId(response.jobChatId);
      }
      setChatAssignmentId(chatId);
      setChatWasCreated(true);

      // Invalidate messaging tab query so new chat appears without manual refresh
      // Only affects this job's messaging tab (JobOverview/Messaging.tsx)
      if (job?.id) {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.MESSAGE_CONVERSATIONS, job.id],
        });
      }

      await updateWorkerInTalentsList();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [job?.id, worker.id]
  );

  const handleSubsequentMessage = useCallback(
    async (chatId: number, message: string) => {
      const response = await sendMessage({ chatId, message });

      if (response?.success && response?.jobChatId && !jobChatId) {
        setJobChatId(response.jobChatId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobChatId]
  );

  const handleSubmitMessage = useCallback(
    async (message: string) => {
      if (!message) return;

      try {
        const chatId = chatAssignmentId || jobChatId;

        if (!chatId) {
          await handleFirstMessage(message);
        } else {
          await handleSubsequentMessage(chatId, message);
        }
      } catch (err) {
        console.error('Failed to send message:', err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chatAssignmentId, jobChatId]
  );

  return (
    <GenericChat
      showWorkerInfo={showWorkerInfo || showPrivateInfo}
      worker={worker}
      conversationId={activeChatId}
      isLoading={isLoadingMessages}
      error={messagesError}
      messages={combinedMessages}
      chatMembers={chatMembers}
      onViewProfile={viewProfile}
      resendMessageMutation={resendMessageMutation}
      onSubmitMessage={handleSubmitMessage}
      isSendingMessage={isSendingMessage}
      // if messagesData?.isSendingMsgAllowed is undefined member can write
      inputDisabledForMember={messagesData?.isSendingMsgAllowed === false}
      onClose={onClose}
      firstUnreadMessageId={originalJobMessage?.firstUnreadMessageId ?? null}
    />
  );
}

export default JobMessageChat;
